import { useEffect, useMemo, useState } from 'react'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import { isNullOrUndefinedOrEmpty } from 'utils'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import {
  hideCampaignConversionTrackingEdit,
  setConversionTrackingId,
  showPreview,
  updateCampaignsConversionTracking,
} from 'features/dashboard/operation/campaignConversionTrackingEdit/campaignConversionTrackingEditSlice'
import { Modal } from 'components/dialog/Modals'
import Table from 'components/table/Table'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import SelectBox from 'components/form/SelectBox'
import classNames from 'classnames'
import NoneAnchor from 'components/NoneAnchor'
import { Link, useParams } from 'react-router-dom'
import FixedTable from 'components/table/FixedTable'
import { shouldBlockConversionTrackingRemoval } from 'features/dashboard/operation/campaignConversionTrackingEdit/campaignConversionTrackingEditUtils'
import { CONVERSION_TRACKING_REQUIRED_MESSAGE } from 'features/ad/campaign/campaignValidation'

const CampaignConversionTrackingEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    campaigns,
    tracks,
    deletedTracks,
    isLoading,
    isLoadingTracks,
    tracksLoadingErrorVisible,
    loadingError,
    hasTalkChannel,
    form: { conversionTrackingId },
    preview: { conversionTrackingId: previewConversionTrackingId },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.campaignConversionTrackingEdit,
    shallowEqual
  )
  const { accountId: adAccountId } = useParams()
  const [trackerPermissionErrorVisible, setTrackerPermissionErrorVisible] =
    useState(false)
  const trackerPlaceholder = useMemo(
    () =>
      conversionTrackingId in deletedTracks ? (
        <>
          <em className="fc_warn">[삭제됨]</em>{' '}
          {deletedTracks[conversionTrackingId]} ({conversionTrackingId})
        </>
      ) : (
        '연동할 픽셀&SDK를 선택하세요.'
      ),
    [conversionTrackingId, deletedTracks]
  )
  const trackNames = useMemo(
    () =>
      tracks.reduce(
        (a, v) => ({
          ...a,
          [v.trackId]: v.trackName,
        }),
        deletedTracks
      ),
    [tracks, deletedTracks]
  )
  const handlePreview = () => {
    dispatch(showPreview())
  }

  const handleConfirm = async ({ preventClose }) => {
    if (
      shouldBlockConversionTrackingRemoval(hasTalkChannel, conversionTrackingId)
    ) {
      preventClose()
      fail(CONVERSION_TRACKING_REQUIRED_MESSAGE)
      return
    }
    const { error, payload } = await dispatch(
      updateCampaignsConversionTracking()
    )
    if (error) {
      preventClose()
      const { errorCode } = payload
      if (errorCode === 103007) {
        setTrackerPermissionErrorVisible(true)
      } else {
        fail(
          '캠페인 전환추적 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
        )
      }
    } else {
      dispatch(refreshDashboard())
      success('캠페인 전환추적 변경사항이 적용되었습니다.')
    }
  }

  useEffect(
    () => () => {
      dispatch(hideCampaignConversionTrackingEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (loadingError) {
      dispatch(hideCampaignConversionTrackingEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          409: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const preview = previewVisible
  const changed = conversionTrackingId !== previewConversionTrackingId

  return (
    <Modal on={visible} className="change_layer trace_layer">
      <PreviewConfirmModal
        title="캠페인 전환추적 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={conversionTrackingId === '' || (!changed && preview)}
        confirmDisabled={conversionTrackingId === '' || (changed && preview)}
        handleClose={() => {
          dispatch(hideCampaignConversionTrackingEdit())
          setTrackerPermissionErrorVisible(false)
        }}>
        <p className="desc_copy">
          전환추적을 위해서 픽셀&amp;SDK 연동이 필요하며, 캠페인 단위로 설정할
          수 있습니다.
          <br />
          연동 가능한 대상은 타겟 관리 &gt;{' '}
          <Link to={`/${adAccountId}/tracker`} className="fc_empty">
            픽셀&amp;SDK 연동 관리
          </Link>
          에서 추가할 수 있습니다.
        </p>
        <div className="box_subcard">
          <strong className="tit_subcard">
            픽셀&amp;SDK <span className="fc_emph">{tracks.length}</span>
          </strong>
          {tracksLoadingErrorVisible ? (
            <div className="nodata_wrap error_data">
              <NoneAnchor className="link_error">
                일시적인 문제로 픽셀&amp;SDK를 불러올 수 없습니다. 다시 시도해
                주세요.
              </NoneAnchor>
            </div>
          ) : isLoading || isLoadingTracks ? (
            <div className="nodata_wrap">
              <div className="inner_nodata">
                <Loading />
              </div>
            </div>
          ) : (
            <SelectBox
              value={conversionTrackingId}
              placeholder={trackerPlaceholder}
              noBlurOnDefault
              error={trackerPermissionErrorVisible}
              onValueChange={v => {
                setTrackerPermissionErrorVisible(false)
                dispatch(setConversionTrackingId(v))
              }}
              disabled={isLoadingTracks}>
              <SelectBox.Item value={null}>연동하지 않기</SelectBox.Item>
              {tracks.map(({ trackId, trackName }) => (
                <SelectBox.Item key={trackId} value={trackId}>
                  {trackName} ({trackId})
                </SelectBox.Item>
              ))}
            </SelectBox>
          )}
          {trackerPermissionErrorVisible && (
            <p className="txt_error">
              사용 불가한 픽셀&SDK 입니다. 다시 선택하세요.
            </p>
          )}
        </div>
        {preview && (
          <FixedTable caption="캠페인 전환추적 변경 이력 표">
            <Table.Head>
              <Table.Column
                title="선택 캠페인"
                className="inner_cell"
                width={199}
              />
              <Table.Column
                title="변경 전"
                className="inner_cell"
                width={168}
              />
              <Table.Column title="변경 후" className="inner_cell" />
            </Table.Head>
            <Table.Body>
              {campaigns.map(
                ({ id, name, conversionTrackingId: beforeTrackId }) => (
                  <Table.Row
                    rowId={id}
                    key={id}
                    className={classNames({
                      update: beforeTrackId !== previewConversionTrackingId,
                    })}>
                    <td>
                      <div className="inner_cell">
                        <span className="txt_limit2">{name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="inner_cell">
                        {isNullOrUndefinedOrEmpty(beforeTrackId)
                          ? '연동하지않기'
                          : `${trackNames[beforeTrackId]} (${beforeTrackId})`}
                      </div>
                    </td>
                    <td>
                      <div className="inner_cell">
                        {isNullOrUndefinedOrEmpty(previewConversionTrackingId)
                          ? '연동하지않기'
                          : `${trackNames[previewConversionTrackingId]} (${previewConversionTrackingId})`}
                      </div>
                    </td>
                  </Table.Row>
                )
              )}
            </Table.Body>
          </FixedTable>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default CampaignConversionTrackingEditModal
